import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config/constants';

interface ContactInfo {
  id: string;
  type: 'email' | 'phone' | 'address' | 'social';
  label: string;
  value: string;
  isActive: boolean;
  order: number;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  }); 

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfo[]>([]);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/contact/active`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data.data || []);
      }
    } catch (error) {
      console.error('Eroare la încărcarea informațiilor de contact:', error);
      // Fallback la valorile hardcoded în caz de eroare
      setContactInfo([
        {
          id: '1',
          type: 'email',
          label: 'Email Contact',
          value: 'contact@lexora.docdill.ro',
          isActive: true,
          order: 1
        }
      ]);
    }
  };

  const getContactIcon = (type: string) => {
    switch (type) {
      case 'email':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'phone':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'address':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'social':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderContactValue = (contact: ContactInfo) => {
    if (contact.type === 'email') {
      return (
        <a href={`mailto:${contact.value}`} className="text-gray-600 hover:text-blue-600 transition">
          {contact.value}
        </a>
      );
    } else if (contact.type === 'phone') {
      return (
        <a href={`tel:${contact.value}`} className="text-gray-600 hover:text-blue-600 transition">
          {contact.value}
        </a>
      );
    } else if (contact.type === 'social') {
      return (
        <a href={contact.value} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition">
          {contact.label}
        </a>
      );
    } else {
      return (
        <span className="text-gray-600">{contact.value}</span>
      );
    }
  };

  const getContactDescription = (type: string) => {
    switch (type) {
      case 'email':
        return 'Răspundem în 24 de ore';
      case 'phone':
        return 'Luni - Vineri, 09:00 - 17:00';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulăm trimiterea formularului
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 2000);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Contactează-ne</h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Suntem aici să te ajutăm! Nu ezita să ne scrii pentru orice întrebare sau sugestie.
              </p>
            </div>
          </div>
        </section>

        {/* Informații de contact și formular */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Informații de contact */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">Informații de Contact</h2>
                
                <div className="space-y-6">
                  {contactInfo.map(contact => (
                    <div key={contact.id} className="flex items-start">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mr-4">
                        {getContactIcon(contact.type)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">{contact.label}</h3>
                        {renderContactValue(contact)}
                        <p className="text-sm text-gray-500">{getContactDescription(contact.type)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Programul */}
                <div className="mt-8 bg-blue-50 p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Program de Lucru</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Luni - Vineri</span>
                      <span className="text-gray-800 font-medium">09:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sâmbătă</span>
                      <span className="text-gray-800 font-medium">10:00 - 14:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duminică</span>
                      <span className="text-gray-800 font-medium">Închis</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Formular de contact */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">Trimite-ne un Mesaj</h2>
                
                {isSubmitted && (
                  <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Mesajul tău a fost trimis cu succes! Îți vom răspunde în cel mai scurt timp.
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nume complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="Introduceți numele complet"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="exemplu@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subiect *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    >
                      <option value="">Selectează un subiect</option>
                      <option value="support">Suport tehnic</option>
                      <option value="suggestion">Sugestie</option>
                      <option value="partnership">Parteneriat</option>
                      <option value="copyright">Drepturi de autor</option>
                      <option value="other">Altceva</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Mesaj *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                      placeholder="Descrieți mesajul dvs. aici..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Se trimite...
                      </div>
                    ) : (
                      'Trimite Mesajul'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Harta */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Locația Noastră</h2>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="h-96">
                {contactInfo.length > 0 ? (
                  <iframe
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(
                      contactInfo.find(contact => contact.type === 'address')?.value || 'București, România'
                    )}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-xl"
                    title="Locația bibliotecii"
                  />
                ) : (
                  // Harta implicită pentru București
                  <iframe
                    src="https://maps.google.com/maps?q=București,România&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-xl"
                    title="Locația bibliotecii"
                  />
                )}
              </div>
            </div>
            
            {/* Informații suplimentare despre locație */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">
                📍 {contactInfo.find(contact => contact.type === 'address')?.value || 'București, România'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    contactInfo.find(contact => contact.type === 'address')?.value || 'București, România'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Deschide în Google Maps
                </a>
                <a
                  href={`https://waze.com/ul?q=${encodeURIComponent(
                    contactInfo.find(contact => contact.type === 'address')?.value || 'București, România'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Navigație Waze
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage; 
